<div data-testid="why-panel" class="why-panel">
  <header class="why-panel__header">
    <h3 class="why-panel__title">{{ insight?.companyName || companyId }}</h3>
    <span *ngIf="tierLabel" class="adb-tier-badge" data-testid="why-header-tier" [attr.data-tier]="tierKey">{{ tierLabel }}</span>
    <button type="button" data-testid="why-close" class="why-panel__close" (click)="closed.emit()" aria-label="Close">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </header>
  <div *ngIf="data && !insight" class="why-panel__empty">No predictions yet for this company</div>
  <div *ngIf="insight" class="why-verdict" data-testid="why-verdict">
    <div class="why-verdict__score" data-testid="why-score" [attr.data-tier]="tierKey">
      <span class="why-verdict__value">{{ insight.healthScore ?? '—' }}</span>
      <span class="why-verdict__of"> / 100</span>
    </div>
    <div class="why-verdict__who">
      <span *ngIf="tierLabel" class="adb-tier-badge" data-testid="why-tier" [attr.data-tier]="tierKey">{{ tierLabel }}</span>
      <p class="why-verdict__meta" data-testid="why-meta">{{ metaLine }}</p>
    </div>
  </div>
  <!-- ITP-E-302-03 (Plan ITP-P-68 D-P5): the panel's action row, directly under the verdict. -->
  <div class="why-panel__actions" data-testid="why-actions">
    <button type="button" class="why-open-360" data-testid="why-open-360" (click)="go('/admin-panel/company-360/' + companyId)">
      <i class="fa-regular fa-building" aria-hidden="true"></i>Open Company 360<i class="fa-regular fa-arrow-right" aria-hidden="true"></i>
    </button>
  </div>
  <ng-container *ngIf="insight">
    <section class="why-block" data-testid="why-block-flags">
      <h4 class="why-block__title">Why it's flagged</h4>
      <p *ngIf="!insight.flags?.length" data-testid="why-not-flagged" class="why-flag__reason">{{ notFlaggedText }}</p>
      <div *ngFor="let flag of insight.flags" data-testid="why-flag" class="why-flag">
        <div class="why-flag__head">
          <span class="flag-chip"><i class="fa-regular" [ngClass]="flagIcon(flag.kind)" aria-hidden="true"></i>{{ flag.label }}</span>
          <span class="why-panel__muted">since {{ flag.firstSeen | date:'d MMM':'UTC' }}</span>
        </div>
        <p class="why-flag__reason">{{ flag.reason }}</p>
        <ul *ngIf="extraFactors(flag).length" class="why-flag__factors">
          <li *ngFor="let factor of extraFactors(flag)" data-testid="why-flag-factor">{{ factor }}</li>
        </ul>
        <div *ngIf="isSuppressed(flag.kind); else flagActions" class="why-flag__suppressed" data-testid="why-suppressed"
             [attr.data-status]="latestAction(flag.kind)?.action">
          <i class="fa-regular" [ngClass]="latestAction(flag.kind)?.action === 'snoozed' ? 'fa-alarm-snooze' : 'fa-circle-check'" aria-hidden="true"></i>
          <span>
            <ng-container *ngIf="latestAction(flag.kind) as a">
              {{ a.action === 'snoozed' ? 'Snoozed until ' + (a.snoozedUntil | date:'d MMM y':'UTC') : 'Handled' }} by {{ a.byName || a.byEmail }} on {{ a.at | date:'d MMM y':'UTC' }}
            </ng-container>
          </span>
          <button type="button" class="why-reopen" data-testid="why-reopen" [disabled]="busy"
                  (click)="reopen(flag.kind)">{{ busy && reopening === flag.kind ? 'Reopening…' : 'Reopen' }}</button>
          <p *ngIf="error && reopening === flag.kind" class="why-panel__error" data-testid="why-reopen-error" role="alert">{{ error }}</p>
        </div>
        <ng-template #flagActions>
          <div class="why-flag__actions" role="group" aria-label="Act on this flag">
            <button type="button" class="why-action adb-natural-width" data-testid="why-snooze" [disabled]="busy"
                    [attr.aria-expanded]="isComposing(flag.kind, 'snooze')" (click)="compose(flag.kind, 'snooze')">
              <i class="fa-regular fa-alarm-snooze" aria-hidden="true"></i>Snooze 30 days
            </button>
            <button type="button" class="why-action adb-natural-width" data-testid="why-handled" [disabled]="busy"
                    [attr.aria-expanded]="isComposing(flag.kind, 'handled')" (click)="compose(flag.kind, 'handled')">
              <i class="fa-regular fa-circle-check" aria-hidden="true"></i>Mark handled
            </button>
          </div>
          <div *ngIf="composingFor(flag.kind) as box" class="why-compose" data-testid="why-compose">
            <label class="why-compose__label" data-testid="why-compose-label" [attr.for]="'why-note-' + flag.kind">{{ box.action === 'snooze' ? 'Why snooze for 30 days?' : 'What was done?' }}</label>
            <textarea class="why-compose__input" data-testid="why-note" rows="3" maxlength="500" [id]="'why-note-' + flag.kind"
                      [placeholder]="box.action === 'snooze' ? 'e.g. Renewal in procurement; CFO confirmed intent.' : 'e.g. Spoke to the tax director; training booked for 12 Oct.'"
                      [(ngModel)]="notes[flag.kind]"></textarea>
            <div class="why-compose__actions">
              <button mat-button type="button" class="adb-natural-width" data-testid="why-compose-cancel" [disabled]="busy" (click)="cancelCompose()">Cancel</button>
              <button mat-flat-button color="primary" type="button" class="adb-natural-width" data-testid="why-compose-confirm" [disabled]="busy"
                      (click)="act(flag.kind, box.action)">{{ box.action === 'snooze' ? 'Snooze' : 'Mark handled' }}</button>
            </div>
            <p *ngIf="error" class="why-panel__error" role="alert">{{ error }}</p>
            <p class="why-compose__hint">The note is optional. Your name and the time are recorded on the flag.</p>
          </div>
        </ng-template>
      </div>
    </section>

    <section class="why-block" data-testid="why-block-health">
      <h4 class="why-block__title">Health score</h4>
      <ul class="why-parts">
        <li *ngFor="let part of insight.healthParts" data-testid="why-health-part" class="why-part">
          <span class="why-part__label">{{ part.label }} <span class="why-part__weight">· {{ part.weight }}%</span></span>
          <span class="why-part__score" data-testid="why-health-part-score">{{ part.score | number:'1.0-0' }}</span>
          <span class="why-bar" aria-hidden="true">
            <span class="why-bar__fill" data-testid="why-health-part-fill" [attr.data-tier]="scoreTier(part.score)" [style.width.%]="part.score"
                  [class.why-bar__fill--zero]="zeroBar(part.score)"></span>
          </span>
          <span class="why-part__detail">{{ part.detail }}</span>
        </li>
      </ul>
    </section>

    <section class="why-block" data-testid="why-block-logins">
      <h4 class="why-block__title">Logins, last 16 weeks</h4>
      <p class="why-sub" data-testid="why-logins-note">{{ loginsNote }}</p>
      <div #chart data-testid="why-logins-chart" class="why-panel__chart" role="img"
           [attr.aria-label]="'Weekly logins for ' + (insight.companyName || companyId)"></div>
    </section>

    <section class="why-block" data-testid="why-block-contract">
      <h4 class="why-block__title">Contract</h4>
      <dl class="why-dl">
        <dt>Contract ends</dt>
        <dd data-testid="why-contract-ends">{{ contractEndsText }}</dd>
        <dt>ARR</dt>
        <dd data-testid="why-arr">
          <ng-container *ngIf="insight.isClient; else noArr">{{ arrText }}</ng-container>
          <ng-template #noArr><span class="why-warn">Unknown — no Client Manager record</span></ng-template>
        </dd>
        <ng-container *ngIf="showLicences">
          <dt>Licences</dt>
          <dd data-testid="why-licences">
            <ng-container *ngIf="licencesReady">
              <span *ngFor="let line of licenceLines" class="why-dl__line" data-testid="why-licence-row">{{ line }}</span>
              <ng-container *ngIf="!licenceLines.length">—</ng-container>
            </ng-container>
          </dd>
        </ng-container>
      </dl>
    </section>

    <section class="why-block" data-testid="why-block-seats">
      <h4 class="why-block__title">Seat use</h4>
      <p class="why-big" data-testid="why-seats"><span class="why-big__value">{{ insight.signals.seatsUsed | number:'1.0-0' }}</span>&ngsp;<span class="why-big__caption">{{ seatsCaption }}</span></p>
      <span *ngIf="insight.signals.seats > 0" class="why-bar" aria-hidden="true">
        <span class="why-bar__fill" data-testid="why-seats-fill" [attr.data-tier]="seatsTier" [style.width.%]="seatsFillPercent"
              [class.why-bar__fill--zero]="zeroBar(seatsFillPercent)"></span>
      </span>
      <p class="why-sub" data-testid="why-seats-note">{{ seatsNote }}</p>
    </section>

    <section class="why-block" data-testid="why-block-xatbot">
      <h4 class="why-block__title">XatBot quality, 30 days</h4>
      <dl *ngIf="insight.signals.unresolvedRate !== null; else fewQuestions" class="why-dl" data-testid="why-xatbot">
        <dt>Questions</dt><dd>{{ insight.signals.xatBotQuestions | number:'1.0-0' }}</dd>
        <dt>Unresolved</dt><dd>{{ insight.signals.unresolvedRate | percent:'1.1-1' }}</dd>
        <dt>Negative feedback</dt><dd>{{ insight.signals.negativeRate | percent:'1.1-1' }}</dd>
        <ng-container *ngIf="spendText as spend"><dt>Spend</dt><dd data-testid="why-spend">{{ spend }}</dd></ng-container>
      </dl>
      <ng-template #fewQuestions>
        <p class="why-sub" data-testid="why-xatbot-unrated">{{ insight.signals.xatBotQuestions ? 'Too few questions to rate.' : 'No XatBot questions in 30 days.' }}</p>
      </ng-template>
    </section>

    <section *ngIf="hasLinks" class="why-block" data-testid="why-links">
      <h4 class="why-block__title">Open in</h4>
      <div class="why-links">
        <button *ngIf="granted('APManagement')" type="button" class="why-link" data-testid="link-company" (click)="go('/admin-panel/company/' + companyId)">
          <i class="fa-regular fa-building" aria-hidden="true"></i>Company details
        </button>
        <button *ngIf="granted('APClientManager') && insight.isClient" type="button" class="why-link" data-testid="link-client-manager" (click)="go('/admin-panel/client-manager')">
          <i class="fa-regular fa-address-card" aria-hidden="true"></i>Client Manager
        </button>
        <button *ngIf="granted('APOnlineUser')" type="button" class="why-link" data-testid="link-online-users" (click)="go('/admin-panel/online-users')">
          <i class="fa-regular fa-signal" aria-hidden="true"></i>Online users
        </button>
        <!-- GEN-E-182-26: the EAT usage link follows the Usage Report app's feature, APUsageReport. -->
        <button *ngIf="granted('APUsageReport')" type="button" class="why-link" data-testid="link-eat-usage" (click)="go('/administration/usage-report/eat-usage-report-by-company/' + companyId)">
          <i class="fa-regular fa-chart-column" aria-hidden="true"></i>EAT usage
        </button>
      </div>
    </section>

    <section *ngIf="data.Actions?.length" class="why-block" data-testid="why-block-history">
      <h4 class="why-block__title">History</h4>
      <ol class="why-log">
        <li *ngFor="let a of data.Actions" data-testid="why-history-row">
          <span class="why-log__what">{{ historyWhat(a) }}</span>
          <span class="why-log__when">{{ flagLabel(a.kind) }} · {{ a.at | date:'d MMM y':'UTC' }}</span>
          <span *ngIf="a.note" class="why-log__note">{{ a.note }}</span>
        </li>
      </ol>
    </section>
  </ng-container>
</div>
